/**
 * The campaign tag vocabulary: categories holding values, each with an icon, a lock and an
 * active state (`api/src/socialsrc_api/routers/tags.py`). Deactivating is a `PATCH` and keeps
 * every assignment; deleting removes the row and its assignments. Every write invalidates the
 * whole campaign's tag reads and its accounts, whose chips render the same names and icons.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiFailureError } from '../../../../lib/http/errors';
import { useApiClient } from '../../../session/client';
import {
  tagCategorySchema,
  tagValueSchema,
  type AlignmentKey,
  type AnalysisScope,
  type TagCategory,
  type TagValue,
} from './schemas';
import { GET_ROUTES, routePath } from '../../../../lib/http/routes';

export const TAG_VOCABULARY_QUERY_KEY = (campaignId: string) =>
  ['campaign', campaignId, 'tags', 'categories'] as const;

function invalidateVocabulary(queryClient: QueryClient, campaignId: string): Promise<unknown> {
  return Promise.all(
    [
      TAG_VOCABULARY_QUERY_KEY(campaignId),
      ['campaign', campaignId, 'tag-categories'],
      ['campaign', campaignId, 'accounts'],
    ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  );
}

export function useTagVocabulary(
  campaignId: string,
): UseQueryResult<TagCategory[], ApiFailureError> {
  const client = useApiClient();

  return useQuery<TagCategory[], ApiFailureError>({
    queryKey: TAG_VOCABULARY_QUERY_KEY(campaignId),
    queryFn: async () => {
      const { categories } = await client.getParsed(
        routePath(GET_ROUTES.campaignTagCategories, { campaignId }),
        GET_ROUTES.campaignTagCategories.schema,
      );
      return categories;
    },
  });
}

export function useCreateCategory(
  campaignId: string,
): UseMutationResult<TagCategory, ApiFailureError, { name: string; icon?: string | null }> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<TagCategory, ApiFailureError, { name: string; icon?: string | null }>({
    mutationFn: (body) =>
      client.postParsed(`/campaigns/${campaignId}/tags/categories`, body, tagCategorySchema),
    onSuccess: () => invalidateVocabulary(queryClient, campaignId),
  });
}

export interface RowPatch {
  name?: string;
  active?: boolean;
  /** `null` clears the icon, so the row inherits again. */
  icon?: string | null;
  /** Alignment values only. */
  analysis_scope?: AnalysisScope;
  /** Categories only: at most one value per account. */
  single?: boolean;
}

export function useUpdateCategory(
  campaignId: string,
): UseMutationResult<TagCategory, ApiFailureError, RowPatch & { categoryId: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<TagCategory, ApiFailureError, RowPatch & { categoryId: string }>({
    mutationFn: ({ categoryId, ...body }) =>
      client.patchParsed(
        `/campaigns/${campaignId}/tags/categories/${categoryId}`,
        body,
        tagCategorySchema,
      ),
    onSuccess: () => invalidateVocabulary(queryClient, campaignId),
  });
}

export function useUpdateValue(
  campaignId: string,
): UseMutationResult<TagValue, ApiFailureError, RowPatch & { valueId: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<TagValue, ApiFailureError, RowPatch & { valueId: string }>({
    mutationFn: ({ valueId, ...body }) =>
      client.patchParsed(`/campaigns/${campaignId}/tags/values/${valueId}`, body, tagValueSchema),
    onSuccess: () => invalidateVocabulary(queryClient, campaignId),
  });
}

export interface NewValue {
  categoryId: string;
  name: string;
  icon?: string | null;
  /** Required in the alignment category: the key the new value counts as. */
  alignment?: AlignmentKey;
}

export function useCreateValue(
  campaignId: string,
): UseMutationResult<TagValue, ApiFailureError, NewValue> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<TagValue, ApiFailureError, NewValue>({
    mutationFn: ({ categoryId, ...body }) =>
      client.postParsed(
        `/campaigns/${campaignId}/tags/categories/${categoryId}/values`,
        body,
        tagValueSchema,
      ),
    onSuccess: () => invalidateVocabulary(queryClient, campaignId),
  });
}

export function useDeleteCategory(
  campaignId: string,
): UseMutationResult<void, ApiFailureError, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiFailureError, string>({
    mutationFn: (categoryId) =>
      client.del(`/campaigns/${campaignId}/tags/categories/${categoryId}`),
    onSuccess: () => invalidateVocabulary(queryClient, campaignId),
  });
}

export function useDeleteValue(
  campaignId: string,
): UseMutationResult<void, ApiFailureError, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiFailureError, string>({
    mutationFn: (valueId) => client.del(`/campaigns/${campaignId}/tags/values/${valueId}`),
    onSuccess: () => invalidateVocabulary(queryClient, campaignId),
  });
}
